// Trang xem cây công việc: task gốc → các task con (đệ quy nhiều cấp).
// Route: /tasks/:id/tree
//
// NGUỒN DỮ LIỆU:
//   Backend không có endpoint trả nguyên cây. GET /api/tasks/{id} chỉ trả
//   subtasks 1 cấp và ở dạng "lite" (thiếu assignee/department).
//   → Trang này tự leo lên gốc theo parentTaskId, rồi nạp đệ quy từng cấp
//     bằng getById để có dữ liệu đầy đủ. Có giới hạn độ sâu và số node
//     để tránh vòng lặp vô hạn nếu dữ liệu bị lỗi tham chiếu.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Tag, Spin, Alert, Button, Progress, Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeft, GitBranch, ExternalLink } from 'lucide-react';
import dayjs from 'dayjs';
import { taskService } from '@/services/taskService';
import { PageHeader, Card } from '@/components/common/PageHeader';
import type { Task } from '@/types';

const MAX_DEPTH = 8;
const MAX_NODES = 300;

const STATUS_META: Record<string, { label: string; color: string }> = {
  NEW: { label: 'Mới', color: 'default' },
  ASSIGNED: { label: 'Đã giao', color: 'blue' },
  IN_PROGRESS: { label: 'Đang làm', color: 'processing' },
  PENDING_REVIEW: { label: 'Chờ duyệt', color: 'gold' },
  COMPLETED: { label: 'Hoàn thành', color: 'success' },
  CANCELLED: { label: 'Đã hủy', color: 'default' },
  OVERDUE: { label: 'Quá hạn', color: 'error' },
  ARCHIVED: { label: 'Lưu trữ', color: 'default' },
};

const PRIORITY_META: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Thấp', color: 'default' },
  MEDIUM: { label: 'Trung bình', color: 'blue' },
  HIGH: { label: 'Cao', color: 'orange' },
  CRITICAL: { label: 'Khẩn cấp', color: 'red' },
};

interface TreeNode extends Task {
  children?: TreeNode[];
}

export function TaskTreePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [root, setRoot] = useState<TreeNode | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setTruncated(false);

      let nodeCount = 0;

      // Nạp đệ quy các cấp con
      const loadChildren = async (task: Task, depth: number): Promise<TreeNode> => {
        const node: TreeNode = { ...task };
        if (depth >= MAX_DEPTH || nodeCount >= MAX_NODES) return node;
        if (!task.subtasks || task.subtasks.length === 0) return node;

        const children: TreeNode[] = [];
        for (const lite of task.subtasks) {
          if (nodeCount >= MAX_NODES) { setTruncated(true); break; }
          nodeCount += 1;
          try {
            const full = await taskService.getById(lite.id);
            children.push(await loadChildren(full, depth + 1));
          } catch {
            // Không có quyền xem task con → vẫn hiển thị bản lite
            children.push({ ...lite });
          }
        }
        node.children = children.length ? children : undefined;
        return node;
      };

      try {
        // Leo lên task gốc
        let current = await taskService.getById(id);
        let guard = 0;
        while (current.parentTaskId && guard < MAX_DEPTH) {
          guard += 1;
          try {
            current = await taskService.getById(current.parentTaskId);
          } catch {
            break; // không có quyền xem cha → dừng ở đây
          }
        }

        const tree = await loadChildren(current, 0);
        if (cancelled) return;
        setRoot(tree);
        setFocusId(id);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message ?? e?.message ?? 'Không tải được cây công việc');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  const columns: ColumnsType<TreeNode> = [
    {
      title: 'Công việc',
      dataIndex: 'title',
      render: (v: string, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            className="mono"
            style={{ fontSize: 11, color: '#7d8a98', flex: '0 0 auto' }}
          >
            {r.code}
          </span>
          <a
            onClick={() => navigate(`/tasks/${r.id}`)}
            style={{
              fontWeight: r.id === focusId ? 600 : 400,
              color: r.id === focusId ? '#1976D2' : undefined,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {v}
          </a>
          {r.id === focusId && (
            <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 10 }}>đang xem</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Người thực hiện',
      width: 180,
      render: (_: unknown, r) => {
        const main = r.assignees?.find(a => a.role === 'MAIN') ?? r.assignees?.[0];
        return main
          ? <span style={{ fontSize: 12 }}>{main.user.fullName}</span>
          : <span style={{ color: '#adb6c0' }}>—</span>;
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 130,
      render: (v: string) => {
        const meta = STATUS_META[v] ?? { label: v, color: 'default' };
        return <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Ưu tiên',
      dataIndex: 'priority',
      width: 115,
      render: (v: string) => {
        const meta = PRIORITY_META[v] ?? { label: v, color: 'default' };
        return <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Tiến độ',
      dataIndex: 'progress',
      width: 130,
      render: (v: number) => (
        <Progress percent={v ?? 0} size="small" status={(v ?? 0) >= 100 ? 'success' : 'active'} />
      ),
    },
    {
      title: 'Hạn',
      dataIndex: 'dueDate',
      width: 110,
      render: (v: string) => (
        <span style={{ fontSize: 12 }}>{v ? dayjs(v).format('DD/MM/YYYY') : '—'}</span>
      ),
    },
    {
      title: '',
      width: 48,
      align: 'center',
      render: (_: unknown, r) => (
        <Tooltip title="Mở chi tiết">
          <Button
            type="text"
            size="small"
            icon={<ExternalLink size={14} />}
            onClick={() => navigate(`/tasks/${r.id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb="CÔNG VIỆC / CÂY CÔNG VIỆC"
        title="Cây công việc"
        subtitle={
          root
            ? <span>Gốc: <strong>{root.code}</strong> — {root.title}</span>
            : 'Xem cấu trúc phân rã công việc theo nhiều cấp'
        }
        extra={
          <Space>
            <Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
              Quay lại
            </Button>
            {id && (
              <Button type="primary" onClick={() => navigate(`/tasks/${id}`)}>
                Chi tiết công việc
              </Button>
            )}
          </Space>
        }
      />

      {error && (
        <Alert type="error" showIcon message="Không tải được cây công việc" description={error} style={{ marginBottom: 16 }} />
      )}

      {truncated && (
        <Alert
          type="warning"
          showIcon
          message={`Cây quá lớn — chỉ hiển thị ${MAX_NODES} công việc đầu tiên.`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <GitBranch size={15} />
            Phân rã công việc
          </span>
        }
        padding={0}
      >
        <Spin spinning={loading}>
          <Table<TreeNode>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={root ? [root] : []}
            pagination={false}
            defaultExpandAllRows
            expandable={{ childrenColumnName: 'children' }}
            rowClassName={(r) => (r.id === focusId ? 'hv-tree-focus' : '')}
          />
        </Spin>
      </Card>
    </div>
  );
}

export default TaskTreePage;
